import { styles } from './styles';
import { tileConfigs, type TileVariant } from './Tile.util';
import { Box, ChevronDown, Pressable, Tag, Text, type BoxRef } from '../../../../../../../../mads';
import { type GettextInterface, useI18n } from '../../../../../../../../shared/I18n';
import { useFullScreenVJActive } from '../../../../../../hooks/useFullScreenVJActive';

import { colors } from '@indeed/ifl-tokens';
import React from 'react';


export interface TileProps {
    variant: TileVariant;
    primaryText: string;
    secondaryText?: string;
    'data-testid'?: string;
    onPress?: () => void;
    isRequired?: boolean;
    isTypicallyRequired?: boolean;
    iconAriaLabel?: string;
}

function getSecondaryText(
    gt: GettextInterface,
    isRequired: boolean | undefined,
    isTypicallyRequired: boolean | undefined,
    secondaryTextFromProps: string,
) {
    if (isRequired) {
        return gt.gettext('(Required)');
    } else if (isTypicallyRequired) {
        return gt.gettext('(Typically Required)');
    }
    return secondaryTextFromProps;
}

function getAccessibilityLabel(
    gt: GettextInterface,
    primaryText: string,
    secondaryText?: string,
    iconAriaLabel?: string,
) {
    if (secondaryText && iconAriaLabel) {
        return gt.pgettext(
            'Concatenated accessibility label for a qualification tile with qualification name and additional details.',
            '{0}, {1}, {2}',
            [primaryText, secondaryText, iconAriaLabel],
        );
    }
    if (secondaryText) {
        return gt.pgettext(
            'Concatenated accessibility label for a qualification tile with qualification name and additional details.',
            '{0}, {1}',
            [primaryText, secondaryText],
        );
    }
    if (iconAriaLabel) {
        return gt.pgettext(
            'Concatenated accessibility label for a qualification tile with qualification name and additional details.',
            '{0}, {1}',
            [primaryText, iconAriaLabel],
        );
    }
    return primaryText;
}

const Tile = React.forwardRef<BoxRef, TileProps>(
    (
        {
            variant,
            primaryText,
            secondaryText: secondaryTextFromProps = '',
            onPress,
            isRequired,
            iconAriaLabel,
            isTypicallyRequired,
        },
        ref,
    ) => {
        const tileConfig = tileConfigs[variant];
        const gt = useI18n();
        const isFullScreenVJActive = useFullScreenVJActive();
        const secondaryText = getSecondaryText(
            gt,
            isRequired,
            isTypicallyRequired,
            secondaryTextFromProps,
        );

        const Icon = tileConfig.iconComponent;

        const accessibilityProps = {
            accessible: true,
            importantForAccessibility: 'yes' as const,
            accessibilityLabel: getAccessibilityLabel(
                gt,
                primaryText,
                secondaryText,
                iconAriaLabel,
            ),
        };

        const TagBody = (
            <Tag
                variant="light"
                status={tileConfig.baseColor}
                data-testid={`${primaryText}-tile`}
                sx={{
                    ...styles.base,
                }}
            >
                <Box
                    sx={[
                        { ...styles.base, ...(Icon ? styles.withIcon : {}) },
                        { flexShrink: 1 },
                    ]}
                >
                    {Icon && (
                        <Box sx={styles.mln4}>
                            <Icon
                                data-icon-type={Icon.displayName}
                                color={colors[tileConfig.baseColor][800]}
                            />
                        </Box>
                    )}
                    <Text
                        sx={{
                            fontWeight: '700',
                            color: colors[tileConfig.baseColor][800],
                            ...styles.wrapText,
                        }}
                    >
                        {primaryText}
                    </Text>
                    {secondaryText && (
                        <Text
                            sx={{
                                fontStyle: 'italic',
                                fontWeight: '400',
                                marginLeft: 2,
                            }}
                            level={1}
                        >
                            {secondaryText}
                        </Text>
                    )}
                    {onPress && (
                        <Box sx={styles.ml4}>
                            <ChevronDown size="xs" color={colors[tileConfig.baseColor][800]} />
                        </Box>
                    )}
                </Box>
            </Tag>
        );

        const FullScreenVJTagBody = (
            <Box sx={styles.fullScreenVJWrapper}>
                <Tag
                    variant="light"
                    status={tileConfig.baseColor}
                    data-testid={`${primaryText}-tile`}
                    sx={{
                        ...styles.base,
                    }}
                >
                    <Box
                        sx={[
                            { ...styles.base, ...(Icon ? styles.withIcon : {}) },
                            { flexShrink: 1 },
                        ]}
                    >
                        {Icon && (
                            <Box sx={styles.mln4}>
                                <Icon
                                    data-icon-type={Icon.displayName}
                                    color={colors[tileConfig.baseColor][800]}
                                />
                            </Box>
                        )}
                        <Box sx={styles.fullScreenVJTextContainer}>
                            <Text
                                sx={{
                                    fontWeight: 'bold',
                                    color: colors[tileConfig.baseColor][800],
                                    flexShrink: 1,
                                    flexWrap: 'wrap',
                                    ...styles.wrapText,
                                }}
                            >
                                {primaryText}
                            </Text>
                            {secondaryText && (
                                <Text
                                    sx={{
                                        ...styles.fullScreenVJSecondaryText,
                                        color: colors[tileConfig.baseColor][800],
                                        marginLeft: 2,
                                    }}
                                >
                                    {secondaryText}
                                </Text>
                            )}
                        </Box>
                        {onPress && (
                            <Box sx={[styles.ml4, styles.fullScreenVJChevronAutoMargin]}>
                                <ChevronDown size="xs" color={colors[tileConfig.baseColor][800]} />
                            </Box>
                        )}
                    </Box>
                </Tag>
            </Box>
        );

        const SelectedTagBody = isFullScreenVJActive ? FullScreenVJTagBody : TagBody;

        return onPress ? (
            <Pressable
                ref={ref}
                accessibilityRole="button"
                onPress={onPress}
                {...accessibilityProps}
            >
                {SelectedTagBody}
            </Pressable>
        ) : (
            <Box ref={ref} {...accessibilityProps}>
                {SelectedTagBody}
            </Box>
        );
    },
);

export default Tile;
export * from './Tile.util';
